import { formatLocaleCurrency, formatLocaleDate } from "@/utils/formatUtils";
import { tBoolean } from "@/utils/translationUtils";
import { Descriptions } from "antd";
import { useTranslation } from "react-i18next";
import { ClientLineDescription } from "../../../../../client/components/ClientLineDescription";
import { ClientFormType } from "../../../../../client/enums";
import { DepositContract } from "../../../../types";
import { ContractAgentsData } from "../../../ContractAgentsData";
import { ContractClientsData } from "../../../ContractClientsData";
import { createCancellationDateDescriptionItem } from "../../../description-items/cancellationDateDescriptionItem";
import { createEffectiveEndDateDescriptionItem } from "../../../description-items/effectiveEndDateDescriptionItem";
import { createNoteDescriptionItem } from "../../../description-items/noteDescriptionItem";

interface Props {
  contract: DepositContract;
}

export const DepositContractDataSection = ({ contract }: Props) => {
  const { t } = useTranslation();

  const client = contract.clients[contract.policyHolderIndex];

  return (
    <Descriptions
      bordered
      column={1}
      styles={{ label: { width: 180, fontWeight: "bold" } }}
      items={[
        {
          label: t("contract.sections.clients"),
          children: client && (
            <ContractClientsData
              items={[
                {
                  label: t(`client.enums.formType.${ClientFormType.POLICY_HOLDER}`),
                  children: <ClientLineDescription client={client} />,
                },
              ]}
            />
          ),
        },
        {
          label: t("contract.sections.contractData"),
          children: (
            <Descriptions
              bordered
              size="small"
              column={3}
              items={[
                {
                  label: t("contract.attrs.mediationReportSignDate"),
                  children: contract.mediationReportSignDate
                    ? formatLocaleDate(contract.mediationReportSignDate)
                    : undefined,
                },
                {
                  label: t("contract.attrs.signDate"),
                  children: formatLocaleDate(contract.signDate),
                },
                createEffectiveEndDateDescriptionItem(contract),
                createCancellationDateDescriptionItem(contract),
                {
                  label: t("contract.attrs.targetAmount"),
                  children: formatLocaleCurrency(contract.targetAmount),
                  span: 2,
                },
                {
                  label: t("contract.attrs.transferredFromOtherBroker"),
                  children: tBoolean(contract.transferredFromOtherBroker),
                },
                {
                  label: t("contract.attrs.transferredToOtherBroker"),
                  children: contract.transferredToOtherBrokerDate
                    ? formatLocaleDate(contract.transferredToOtherBrokerDate)
                    : undefined,
                  span: 2,
                },
                createNoteDescriptionItem(contract),
              ]}
            />
          ),
        },
        {
          label: t("contract.sections.agents"),
          children: <ContractAgentsData contract={contract} />,
        },
      ]}
    />
  );
};
